import React, { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import VERIFY_GUEST_TOKEN from '../../graphql/mutations/traineeships/verifyGuestToken';
import GET_OUTGOING_TRAINEESHIP from '../../graphql/queries/traineeships/getOutgoingTraineeship';
import GuestSingleViewTraineeship from '../Traineeships/GuestView';

export const VerifyGuest = () => {
  const [searchParams] = useSearchParams();
  
  // Use ref to preserve token even if searchParams changes
  const tokenRef = useRef(null);
  
  // Get token from URL on first mount
  if (!tokenRef.current) {
    const urlToken = searchParams.get('token');
    if (urlToken) {
      tokenRef.current = urlToken;
    }
  }
  
  const token = tokenRef.current;

  const { data, loading, error } = useQuery(VERIFY_GUEST_TOKEN, {
    variables: { token },
    skip: !token,
    fetchPolicy: 'network-only',
  });

  // Fetch traineeship data to get receiving_contact_person name
  const { data: traineeshipData, loading: traineeshipLoading } = useQuery(GET_OUTGOING_TRAINEESHIP, {
    variables: { id: data?.verifyGuestToken?.traineeshipID },
    skip: !data?.verifyGuestToken?.traineeshipID,
  });

  // No token
  if (!token) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh', 
        backgroundColor: '#f9fafb' 
      }}>
        <div style={{ 
          maxWidth: '28rem', 
          width: '100%', 
          backgroundColor: 'white', 
          borderRadius: '0.5rem', 
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
          padding: '2rem' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              margin: '0 auto', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '3rem', 
              width: '3rem', 
              borderRadius: '9999px', 
              backgroundColor: '#fef3c7', 
              marginBottom: '1rem' 
            }}>
              <svg
                style={{ height: '1.5rem', width: '1.5rem', color: '#d97706' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
              Missing Token
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
              No verification token found in the URL. Please use the link from your email.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Loading
  if (loading || traineeshipLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh', 
        backgroundColor: '#f9fafb' 
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            animation: 'spin 1s linear infinite', 
            borderRadius: '9999px', 
            height: '3rem', 
            width: '3rem', 
            border: '4px solid #e5e7eb', 
            borderTopColor: '#2563eb', 
            margin: '0 auto 1rem' 
          }}></div>
          <p style={{ color: '#6b7280' }}>Verifying your token...</p>
        </div>
      </div>
    );
  }

  // Error/Invalid
  const hasError = error || !data || !data.verifyGuestToken || data.verifyGuestToken.valid === false;
  
  if (hasError) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh', 
        backgroundColor: '#f9fafb' 
      }}>
        <div style={{ 
          maxWidth: '28rem', 
          width: '100%', 
          backgroundColor: 'white', 
          borderRadius: '0.5rem', 
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', 
          padding: '2rem' 
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              margin: '0 auto', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              height: '3rem', 
              width: '3rem', 
              borderRadius: '9999px', 
              backgroundColor: '#fee2e2', 
              marginBottom: '1rem' 
            }}>
              <svg
                style={{ height: '1.5rem', width: '1.5rem', color: '#dc2626' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
              Invalid Token
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
              The verification link is invalid or has expired.
            </p>
            {error && (
              <p style={{ fontSize: '0.875rem', color: '#ef4444', marginTop: '1rem' }}>
                Error: {error.message}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const email = data.verifyGuestToken.email;
  const traineeshipID = data.verifyGuestToken.traineeshipID;
  
  // Get the full name from receiving_contact_person
  const receivingContactPerson = traineeshipData?.traineeship?.receiving_contact_person;
  const guestName = receivingContactPerson?.field_per_firstname && receivingContactPerson?.field_per_lastname
    ? `${receivingContactPerson.field_per_firstname} ${receivingContactPerson.field_per_lastname}`.trim()
    : email.split('@')[0]; // Fallback to email prefix if name not available

  return (
    <GuestSingleViewTraineeship 
      traineeshipID={traineeshipID}
      guestEmail={email}
      guestName={guestName}
      verifiedTraineeshipID={traineeshipID}
    />
  );
};